import {changeIssuesTabCounter} from '@github-ui/issues-tab-counter'
import type {AssigneePickerAssignee} from '@github-ui/item-picker-assignee-tsq'
import type {IssueTypePickerIssueType} from '@github-ui/item-picker-issue-type'
import type {LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {getListItemIdentity} from '@github-ui/shared-dashboard-lists/shared/helpers/item-identity'
import type {IssueListItem} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type RepoIssuesBulkOptimisticUpdate = Readonly<{
  addedLabels?: ReadonlyArray<
    Pick<LabelPickerLabel, 'id' | 'name' | 'nameHTML' | 'color'> & Partial<Pick<LabelPickerLabel, 'description'>>
  >
  addedAssignees?: ReadonlyArray<
    Pick<AssigneePickerAssignee, 'id' | 'login' | 'avatarUrl'> &
      Partial<Pick<AssigneePickerAssignee, 'name' | 'profileResourcePath' | 'isCopilot' | 'isAgent'>>
  >
  milestone?: Pick<MilestonePickerMilestone, 'id' | 'title' | 'url'> | null
  issueType?: Pick<IssueTypePickerIssueType, 'id' | 'name' | 'color'> | null
}>

export type RepoIssuesBulkUpdate =
  | Readonly<{state: 'OPEN'; stateReason?: never}>
  | Readonly<{state: 'CLOSED'; stateReason: 'COMPLETED' | 'NOT_PLANNED'}>
  | Readonly<{addedLabelIds: readonly string[]; removedLabelIds: readonly string[]}>
  | Readonly<{applyAssigneeIds: readonly string[]; removeAssigneeIds: readonly string[]}>
  | Readonly<{addToProjectIds: readonly string[]; removeFromProjectIds: readonly string[]}>
  | Readonly<{milestoneId: string}>
  | Readonly<{clearMilestone: true}>
  | Readonly<{issueTypeId: string}>
  | Readonly<{clearIssueType: true}>

export type RepoIssuesBulkTarget = Readonly<{ids: readonly string[]}> | Readonly<{query: string; repositoryId: string}>

export interface RepoIssuesBulkUpdateResult {
  failedIssueNodeIds: string[]
}

export interface RepoIssuesBulkUpdateProgress {
  state: 'PENDING' | 'QUEUED' | 'STARTED'
  percentage: number
  failedCount: number
}

export type RepoIssuesBulkJobStatus =
  | {status: 'pending'; progress: RepoIssuesBulkUpdateProgress}
  | {status: 'completed'; result: RepoIssuesBulkUpdateResult}
  | {status: 'failed'; errorMessage: string}
  | {status: 'missing'}

export const REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE =
  'The update timed out, but some changes might have been applied. Refresh the page before trying again.'

export const REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS = 5 * 60 * 1000

type IssueForTabCounter = Pick<IssueListItem, 'globalId' | 'id' | 'itemType' | 'state'>

export function applyRepoIssuesBulkOptimisticUpdate(
  issue: IssueListItem,
  update: RepoIssuesBulkUpdate,
  optimisticUpdate?: RepoIssuesBulkOptimisticUpdate,
): IssueListItem {
  if ('state' in update) {
    return {
      ...issue,
      state: update.state,
      stateReason: update.state === 'OPEN' ? 'REOPENED' : update.stateReason,
      closed: update.state === 'CLOSED',
      closedAt: update.state === 'OPEN' ? null : issue.closed ? issue.closedAt : new Date().toISOString(),
    }
  }
  if ('addedLabelIds' in update) {
    const removedIds = new Set(update.removedLabelIds)
    const retainedLabels = issue.labels.filter(label => !removedIds.has(String(label.id)))
    const retainedIds = new Set(retainedLabels.map(label => String(label.id)))
    const addedLabels = (optimisticUpdate?.addedLabels ?? [])
      .filter(label => !retainedIds.has(label.id))
      .map(label => ({
        id: label.id,
        name: label.name,
        nameHtml: label.nameHTML,
        color: label.color,
        description: label.description ?? undefined,
      }))
    return {...issue, labels: [...retainedLabels, ...addedLabels]}
  }
  if ('applyAssigneeIds' in update) {
    const removedIds = new Set(update.removeAssigneeIds)
    const retainedAssignees = issue.assignees.filter(assignee => !removedIds.has(String(assignee.id)))
    const retainedIds = new Set(retainedAssignees.map(assignee => String(assignee.id)))
    const addedAssignees = (optimisticUpdate?.addedAssignees ?? [])
      .filter(assignee => !retainedIds.has(assignee.id))
      .map(assignee => ({
        id: assignee.id,
        login: assignee.login,
        name: assignee.name ?? undefined,
        avatarUrl: assignee.avatarUrl,
        profileResourcePath: assignee.profileResourcePath ?? '',
        isCopilot: assignee.isCopilot ?? false,
        isAgent: assignee.isAgent ?? false,
      }))
    return {...issue, assignees: [...retainedAssignees, ...addedAssignees]}
  }
  if ('milestoneId' in update || 'clearMilestone' in update) {
    return {...issue, milestone: optimisticUpdate?.milestone ?? null}
  }
  if ('issueTypeId' in update || 'clearIssueType' in update) {
    return {...issue, issueType: optimisticUpdate?.issueType ?? null}
  }
  return issue
}

export function updateIssuesTabCounter({
  issues,
  update,
  failedIssueIds,
  allMatching,
  direction = 1,
}: {
  issues: IssueForTabCounter[]
  update: RepoIssuesBulkUpdate
  failedIssueIds: ReadonlySet<string>
  allMatching: boolean
  direction?: 1 | -1
}): void {
  let delta = 0
  if (!allMatching && 'state' in update) {
    for (const issue of issues) {
      if (failedIssueIds.has(getListItemIdentity(issue))) continue
      if (issue.state === 'OPEN' && update.state === 'CLOSED') delta -= 1
      else if (issue.state === 'CLOSED' && update.state === 'OPEN') delta += 1
    }
  }
  if (delta !== 0) changeIssuesTabCounter(delta * direction)
}

function mutationJobId(payload: unknown): string {
  if (!payload || typeof payload !== 'object') throw new Error('Bulk update returned an invalid response')
  if ('errors' in payload && Array.isArray(payload.errors) && payload.errors.length > 0) {
    const firstError = payload.errors[0]
    throw new Error(
      firstError && typeof firstError === 'object' && 'message' in firstError && typeof firstError.message === 'string'
        ? firstError.message
        : 'Bulk update failed',
    )
  }

  if (!('data' in payload) || !payload.data || typeof payload.data !== 'object') {
    throw new Error('Bulk update returned an invalid response')
  }
  const data = payload.data as Record<string, unknown>
  const mutation = data.updateIssuesBulk ?? data.updateIssuesBulkByQuery
  if (!mutation || typeof mutation !== 'object' || !('jobId' in mutation) || typeof mutation.jobId !== 'string') {
    throw new Error('Bulk update did not return a job ID')
  }
  return mutation.jobId
}

interface ExecutionError {
  node_id: string
  message: string
}

function isExecutionError(value: unknown): value is ExecutionError {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'node_id' in value &&
    typeof value.node_id === 'string' &&
    'message' in value &&
    typeof value.message === 'string',
  )
}

function jobFromPayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object' || !('job' in payload)) {
    throw new Error('Bulk update returned an invalid job response')
  }
  const job = payload.job
  if (!job || typeof job !== 'object') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return job as Record<string, unknown>
}

function failedIssueNodeIds(payload: unknown, submittedIds?: ReadonlySet<string>): string[] {
  const job = jobFromPayload(payload)
  if (!Array.isArray(job.execution_errors)) throw new Error('Bulk update returned an invalid job response')

  const failedIds = new Set<string>()
  for (const executionError of job.execution_errors) {
    if (!isExecutionError(executionError)) {
      throw new Error('Bulk update returned an invalid job response')
    }
    if (submittedIds && !submittedIds.has(executionError.node_id)) {
      throw new Error('Bulk update returned an unexpected failed issue')
    }
    failedIds.add(executionError.node_id)
  }
  return [...failedIds]
}

function jobProgress(payload: unknown): RepoIssuesBulkUpdateProgress {
  const job = jobFromPayload(payload)
  const state = typeof job.state === 'string' ? job.state.toUpperCase() : ''
  if (state !== 'PENDING' && state !== 'QUEUED' && state !== 'STARTED') {
    throw new Error('Bulk update returned an invalid job response')
  }
  return {
    state,
    percentage: typeof job.percentage === 'number' ? job.percentage : 0,
    failedCount: Array.isArray(job.execution_errors) ? job.execution_errors.length : 0,
  }
}

export async function startIssuesBulkUpdate({
  target,
  update,
}: {
  target: RepoIssuesBulkTarget
  update: RepoIssuesBulkUpdate
}): Promise<string> {
  if ('ids' in target && (target.ids.length < 1 || target.ids.length > 1000)) {
    throw new Error('Select between 1 and 1,000 issues')
  }

  const abortController = new AbortController()
  const timeout = setTimeout(() => abortController.abort(), REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS)

  try {
    const response = await reactFetchJSON('/_ghui/item-pickers/bulk_actions', {
      method: 'POST',
      body: {...target, ...update},
      signal: abortController.signal,
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)

    return mutationJobId(await response.json())
  } catch (error) {
    if (abortController.signal.aborted) throw new Error(REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE)
    throw error
  } finally {
    clearTimeout(timeout)
  }
}

export async function fetchIssuesBulkJobStatus({
  jobId,
  submittedIds,
  signal,
}: {
  jobId: string
  submittedIds?: readonly string[]
  signal?: AbortSignal
}): Promise<RepoIssuesBulkJobStatus> {
  const response = await reactFetchJSON(`/_ghui/item-pickers/bulk_actions/${encodeURIComponent(jobId)}`, {
    headers: {Accept: 'application/json'},
    signal,
  })
  if (response.status === 404 || response.status === 410) return {status: 'missing'}
  if (response.status === 500) {
    const job = jobFromPayload(await response.json())
    return {
      status: 'failed',
      errorMessage: typeof job.error_message === 'string' ? job.error_message : 'Bulk update had an error',
    }
  }
  if (!response.ok) throw new Error(`HTTP ${response.status}`)

  const payload = await response.json()
  if (response.status === 202) return {status: 'pending', progress: jobProgress(payload)}
  return {
    status: 'completed',
    result: {failedIssueNodeIds: failedIssueNodeIds(payload, submittedIds ? new Set(submittedIds) : undefined)},
  }
}
